// TelcoTrack — attachment phase keys (photos.status_phase, material_usage.phase_code, workflow notes).
//
// BUDOVA / ROZVADEC parallel milestones use synthetic keys `{TYPE}.MILESTONE.{id}` because they
// are not linear workflow status codes (e.g. Zřízené VR vs aggregate Připojené plasty on PHASE.0).

import {
  BUDOVA_MILESTONE_IDS,
  BUDOVA_MILESTONE_LABELS,
  allMilestonesComplete,
  getRequiredMilestones,
  getVisibleMilestoneIds,
  inferBudovaBuildingType,
  isBudovaMilestoneHostPhase,
  shouldShowBudovaProgressMilestones,
  isMilestoneComplete,
  type BudovaBuildingType,
  type BudovaMilestoneEngineContext,
  type BudovaMilestoneId,
  type BudovaMilestonesFlags,
} from './budova-milestone-engine';
import {
  ROZVADEC_LEGACY_MILESTONE_IDS,
  ROZVADEC_MILESTONE_LABELS,
  ROZVADEC_REQUIRED_MILESTONES,
  normalizeRozvadecMilestoneId,
  isRozvadecMilestoneHostPhase,
  type RozvadecMilestoneId,
  type RozvadecMilestonesFlags,
} from './rozvadec-milestone-engine';
import { DRAWING_IDS, getDrawingLabel } from '../drawing.constants';
import { getPhaseName, normalizeObjectTypeForLookup, PHASE_CODE, PHASE_NAME, resolvePhaseLabel } from './phase-codes';
import { isSamePhase, nextOptionsFromCode } from './workflow-model';

/** Prefix for milestone attachment keys stored in DB (`photos.status_phase`, `material_usage.phase_code`). */
export const BUDOVA_MILESTONE_ATTACHMENT_PREFIX = 'BUDOVA.MILESTONE.';

export function buildBudovaMilestoneAttachmentPhaseKey(milestoneId: BudovaMilestoneId): string {
  return `${BUDOVA_MILESTONE_ATTACHMENT_PREFIX}${milestoneId}`;
}

export function parseBudovaMilestoneFromAttachmentPhaseKey(
  phaseKey: string | null | undefined,
): BudovaMilestoneId | null {
  const key = (phaseKey ?? '').trim();
  if (!key.startsWith(BUDOVA_MILESTONE_ATTACHMENT_PREFIX)) return null;
  const id = key.slice(BUDOVA_MILESTONE_ATTACHMENT_PREFIX.length).trim();
  return (BUDOVA_MILESTONE_IDS as readonly string[]).includes(id) ? (id as BudovaMilestoneId) : null;
}

/** Prefix for ROZVADEC welding milestone attachment keys. */
export const ROZVADEC_MILESTONE_ATTACHMENT_PREFIX = 'ROZVADEC.MILESTONE.';

export function buildRozvadecMilestoneAttachmentPhaseKey(milestoneId: RozvadecMilestoneId): string {
  return `${ROZVADEC_MILESTONE_ATTACHMENT_PREFIX}${milestoneId}`;
}

export function parseRozvadecMilestoneFromAttachmentPhaseKey(
  phaseKey: string | null | undefined,
): RozvadecMilestoneId | null {
  const key = (phaseKey ?? '').trim();
  if (!key.startsWith(ROZVADEC_MILESTONE_ATTACHMENT_PREFIX)) return null;
  const id = key.slice(ROZVADEC_MILESTONE_ATTACHMENT_PREFIX.length).trim();
  return normalizeRozvadecMilestoneId(id);
}

/** UI label for any attachment phase key (workflow code or parallel milestone synthetic key). */
export function resolveAttachmentPhaseLabel(
  phaseKey: string | null | undefined,
  objectType?: string | null,
): string {
  const key = (phaseKey ?? '').trim();
  if (!key) return '';
  const budovaMilestoneId = parseBudovaMilestoneFromAttachmentPhaseKey(key);
  if (budovaMilestoneId) return BUDOVA_MILESTONE_LABELS[budovaMilestoneId];
  const rozvadecMilestoneId = parseRozvadecMilestoneFromAttachmentPhaseKey(key);
  if (rozvadecMilestoneId) return ROZVADEC_MILESTONE_LABELS[rozvadecMilestoneId];
  // Papírová smlouva se ukládá pod výkres Akvizice (`DRAWING_IDS.AKVIZICE`), ne pod lineární fázi budovy.
  if (key === DRAWING_IDS.AKVIZICE) return getDrawingLabel(DRAWING_IDS.AKVIZICE);
  return resolvePhaseLabel(key, objectType);
}

export interface ResolveAttachmentPhaseKeyParams {
  objectType?: string | null;
  objectStatus?: string | null;
  /** Active BUDOVA milestone (popup selection or inferred open milestone). */
  budovaMilestoneId?: BudovaMilestoneId | null;
  /** Active ROZVADEC milestone (popup selection or inferred open milestone). */
  rozvadecMilestoneId?: RozvadecMilestoneId | null;
  /** User override (fotky: „Nahrát k jiné fázi"). Materiál už override UI nemá. */
  explicitPhaseKey?: string | null;
  /** BUDOVA building type — enables milestone inference from object state. */
  budovaBuildingType?: BudovaBuildingType | null;
  /** BUDOVA milestone flags / branch context for open-milestone inference. */
  budovaMilestoneCtx?: BudovaMilestoneEngineContext | null;
  /** ROZVADEC milestone flags for open-milestone inference. */
  rozvadecMilestones?: RozvadecMilestonesFlags | null;
  /** Object metadata — used to infer BUDOVA building type when omitted. */
  objectMetadata?: Record<string, unknown> | null;
}

export interface InferredAttachmentMilestoneIds {
  budovaMilestoneId: BudovaMilestoneId | null;
  rozvadecMilestoneId: RozvadecMilestoneId | null;
}

/**
 * When parallel milestones are active (host phase + incomplete), return the first open
 * milestone in required order. Explicit UI selection wins via `selected*` params.
 */
export function inferAttachmentMilestoneIds(
  params: ResolveAttachmentPhaseKeyParams,
): InferredAttachmentMilestoneIds {
  const selectedBudova = params.budovaMilestoneId ?? null;
  if (selectedBudova) {
    return { budovaMilestoneId: selectedBudova, rozvadecMilestoneId: null };
  }
  // ROZVADEC is phases only (Svařený) — never stamp / infer `ROZVADEC.MILESTONE.*`.
  const objectType = normalizeObjectTypeForLookup(String(params.objectType ?? ''));
  const status = (params.objectStatus ?? '').trim();

  if (objectType === 'BUDOVA') {
    const buildingType =
      params.budovaBuildingType
      ?? inferBudovaBuildingType({
        metadata: params.objectMetadata ?? null,
        status,
      });
    const ctx: BudovaMilestoneEngineContext = params.budovaMilestoneCtx ?? {};
    if (
      buildingType
      && shouldShowBudovaProgressMilestones(buildingType, status)
      && !allMilestonesComplete(buildingType, ctx)
    ) {
      const open = getVisibleMilestoneIds(buildingType, { ...ctx, currentStatus: status }).find(
        (id) => !isMilestoneComplete(buildingType, id, ctx),
      );
      if (open) return { budovaMilestoneId: open, rozvadecMilestoneId: null };
    }
  }

  return { budovaMilestoneId: null, rozvadecMilestoneId: null };
}

/**
 * THE single attachment-phase rule for photos AND material (creation-time stamp).
 *
 * An attachment is stamped with the phase it CONFIRMS. Priority (deterministic — derived
 * only from the workflow model + current status, never from fragile UI hints):
 *   1. explicit user override (fotky: „Nahrát k jiné fázi")
 *   2. active / inferred BUDOVA milestone key when parallel milestones are open
 *      (ROZVADEC is phases only — Svařený, never a Provaření milestone stamp)
 *   3. the next linear phase after the current status (the phase this attachment documents
 *      reaching, e.g. VYKOP Nevykopaný → stamp Vykopaný) — matches `material_usage.phase_code`
 *      backfill (migration 111 = `new_values.status`) and the phase-transition photo gate
 *   4. the current status when there is no next phase (terminal phase / standalone edit)
 *
 * We NEVER fall back to the workflow's initial phase — that fragile collapse (empty status
 * or missing next-phase hint) was the source of the spurious "Nevykopaný" stamp. When the
 * status is unknown we return `''` (UI shows „Bez přiřazení fáze"; backend fills the phase
 * on commit from `new_values.status`).
 */
export function resolveAttachmentPhaseKey(params: ResolveAttachmentPhaseKeyParams): string {
  const explicit = (params.explicitPhaseKey ?? '').trim();
  if (explicit) return explicit;

  const inferred = inferAttachmentMilestoneIds(params);
  if (inferred.budovaMilestoneId) {
    return buildBudovaMilestoneAttachmentPhaseKey(inferred.budovaMilestoneId);
  }
  if (inferred.rozvadecMilestoneId) {
    return buildRozvadecMilestoneAttachmentPhaseKey(inferred.rozvadecMilestoneId);
  }

  const status = (params.objectStatus ?? '').trim();
  if (!status) return '';

  const next = nextOptionsFromCode(status)[0]?.code?.trim();
  return next || status;
}

/** Legacy Provaření milestone keys that count as the linear Svařený photo bucket. */
export function rozvadecSvarenyPhotoAliasKeys(): readonly string[] {
  return [
    buildRozvadecMilestoneAttachmentPhaseKey('PROVARENI'),
    ...ROZVADEC_LEGACY_MILESTONE_IDS.map(
      (legacy) => `${ROZVADEC_MILESTONE_ATTACHMENT_PREFIX}${legacy}`,
    ),
    ROZVADEC_MILESTONE_LABELS.PROVARENI,
  ];
}

/** True when a stamp is linear Svařený or leftover Provaření (milestone / display name). */
export function isRozvadecSvarenyAttachmentPhase(phaseKey: string | null | undefined): boolean {
  const key = (phaseKey ?? '').trim();
  if (!key) return false;
  if (isSamePhase(key, PHASE_CODE.ROZVADEC_PROVARENE)) return true;
  return photoMatchesAttachmentPhaseKeys(key, rozvadecSvarenyPhotoAliasKeys());
}

/** INFORMACE grouping — Provaření-tagged rows share the Svařený bucket. */
export function canonicalInfoOverviewPhaseKey(phaseKey: string | null | undefined): string {
  const key = (phaseKey ?? '').trim();
  return isRozvadecSvarenyAttachmentPhase(key) ? PHASE_CODE.ROZVADEC_PROVARENE : key;
}

/** INFORMACE / photo section label — leftover Provaření shows as Svařený. */
export function resolveInfoOverviewPhaseLabel(
  phaseKey: string | null | undefined,
  objectType?: string | null,
): string {
  const canonical = canonicalInfoOverviewPhaseKey(phaseKey);
  if (isSamePhase(canonical, PHASE_CODE.ROZVADEC_PROVARENE)) {
    return getPhaseName(PHASE_CODE.ROZVADEC_PROVARENE) || PHASE_NAME.ROZVADEC_PROVARENE;
  }
  return resolveAttachmentPhaseLabel(phaseKey, objectType);
}

/** Keys that satisfy photo requirement when confirming linear phase `targetPhaseCode`. */
export function attachmentPhaseKeysForPhaseConfirm(
  targetPhaseCode: string | null | undefined,
): readonly string[] {
  const target = (targetPhaseCode ?? '').trim();
  if (!target) return [];
  if (isSamePhase(target, PHASE_CODE.ROZVADEC_PROVARENE)) {
    return [target, ...rozvadecSvarenyPhotoAliasKeys()];
  }
  return [target];
}

/** Keys that satisfy photo requirement when completing BUDOVA milestone. */
export function attachmentPhaseKeysForMilestoneConfirm(
  milestoneId: BudovaMilestoneId | null | undefined,
): readonly string[] {
  if (!milestoneId) return [];
  return [buildBudovaMilestoneAttachmentPhaseKey(milestoneId)];
}

/** Keys that satisfy photo requirement when completing ROZVADEC welding milestone. */
export function attachmentPhaseKeysForRozvadecMilestoneConfirm(
  milestoneId: RozvadecMilestoneId | string | null | undefined,
): readonly string[] {
  const id = normalizeRozvadecMilestoneId(milestoneId);
  if (!id) return [];
  return [
    buildRozvadecMilestoneAttachmentPhaseKey(id),
    ...ROZVADEC_LEGACY_MILESTONE_IDS.map((legacy) =>
      `${ROZVADEC_MILESTONE_ATTACHMENT_PREFIX}${legacy}`,
    ),
  ];
}

/** True when photo `statusPhase` matches any allowed key for phase/milestone confirm. */
export function photoMatchesAttachmentPhaseKeys(
  photoStatusPhase: string | null | undefined,
  allowedKeys: readonly string[],
): boolean {
  const phase = (photoStatusPhase ?? '').trim();
  if (!phase || allowedKeys.length === 0) return false;
  return allowedKeys.some((k) => k.trim() === phase);
}

/** History row shape for attachment phase resolution (API snake_case + camelCase). */
export interface HistoryEntryAttachmentPhaseSource {
  changeType?: string | null;
  newValues?: Record<string, unknown> | null;
  new_values?: Record<string, unknown> | null;
  statusTransitionTo?: string | null;
  status_transition_to?: string | null;
}

/**
 * Target attachment phase from an `object_history` row after commit.
 * `status_change` / `update` → `new_values.status` (phaseTo), never `status_transition_from`.
 */
export function historyEntryAttachmentPhaseKey(
  entry: HistoryEntryAttachmentPhaseSource | null | undefined,
): string | null {
  if (!entry) return null;
  const nv = (entry.newValues ?? entry.new_values) as Record<string, unknown> | null | undefined;
  const fromNewValues = typeof nv?.['status'] === 'string' ? nv['status'].trim() : '';
  if (fromNewValues) return fromNewValues;
  const to = String(entry.statusTransitionTo ?? entry.status_transition_to ?? '').trim();
  return to || null;
}

export interface DiaryPhotoAttachmentMatchParams {
  objectType?: string | null;
  phaseTo?: string | null;
  objectMetadata?: Record<string, unknown> | null;
}

/**
 * All `photos.status_phase` keys that belong to a work-summary / history row.
 * Linear commits: `[phaseTo]`. Milestone host phase: also all parallel milestone keys.
 * Milestone completion row (`phaseTo` = `*.MILESTONE.*`): `[phaseTo]` only.
 */
export function diaryPhotoAttachmentPhaseKeysForEntry(
  params: DiaryPhotoAttachmentMatchParams,
): readonly string[] {
  const phaseTo = (params.phaseTo ?? '').trim();
  if (!phaseTo) return [];

  if (
    parseBudovaMilestoneFromAttachmentPhaseKey(phaseTo)
    || parseRozvadecMilestoneFromAttachmentPhaseKey(phaseTo)
  ) {
    return [phaseTo];
  }

  const keys = new Set<string>([phaseTo]);
  const objectType = normalizeObjectTypeForLookup(String(params.objectType ?? ''));
  const meta = params.objectMetadata ?? null;

  if (objectType === 'BUDOVA') {
    const buildingType = inferBudovaBuildingType({ metadata: meta, status: phaseTo });
    if (buildingType && isBudovaMilestoneHostPhase(buildingType, phaseTo)) {
      for (const id of getRequiredMilestones(buildingType)) {
        keys.add(buildBudovaMilestoneAttachmentPhaseKey(id));
      }
    }
  }

  if (objectType === 'ROZVADEC' && isSamePhase(phaseTo, PHASE_CODE.ROZVADEC_PROVARENE)) {
    for (const alias of rozvadecSvarenyPhotoAliasKeys()) keys.add(alias);
  }

  if (objectType === 'ROZVADEC' && isRozvadecMilestoneHostPhase(phaseTo)) {
    for (const id of ROZVADEC_REQUIRED_MILESTONES) {
      keys.add(buildRozvadecMilestoneAttachmentPhaseKey(id));
    }
    for (const legacy of ROZVADEC_LEGACY_MILESTONE_IDS) {
      keys.add(`${ROZVADEC_MILESTONE_ATTACHMENT_PREFIX}${legacy}`);
    }
  }

  return [...keys];
}
